import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

import './Backdrop.scss';

const ESC_KEY = 27;

const defaultFn = () => {};

const Backdrop = ({
  isOpen = false,
  closeOnClick = false,
  handleClose = defaultFn,
  children
}) => {
  const backdropRef = useRef(null);
  const el = useRef(
    typeof document !== 'undefined' ? document.createElement('div') : null
  );
  const container = useRef(
    typeof document !== 'undefined' ? document.querySelector('body') : null
  );

  const handleCloseOnClick = e => {
    if (e.target === el.current) {
      handleClose();
    }
  };

  const handleCloseOnKeyDown = e => {
    if (e.keyCode === ESC_KEY && isOpen) {
      handleClose();
    }
  };

  const handleBackdropClick = e => {
    e && e.stopPropagation();
    if (e.target === backdropRef.current) {
      handleClose();
    }
  };

  useEffect(() => {
    if (!el.current || !container.current) return;

    const currentEl = el.current;
    const currentContainer = container.current;

    if (closeOnClick) {
      currentEl.addEventListener('click', handleCloseOnClick);
    }
    currentContainer.insertBefore(currentEl, currentContainer.firstChild);

    window.addEventListener('keydown', handleCloseOnKeyDown);

    // eslint-disable-next-line consistent-return
    return () => {
      if (closeOnClick) {
        currentEl.removeEventListener('click', handleCloseOnClick);
      }
      currentContainer.removeChild(currentEl);
      window.removeEventListener('keydown', handleCloseOnKeyDown);
    };

    // Cleanup logic is already handled above, no return statement needed here.
  }, [closeOnClick]);

  useEffect(() => {
    if (!el.current || !container.current) return;

    if (isOpen) {
      el.current.classList.add('backdrop');
      container.current.classList.add('backdrop--open');
    } else {
      el.current.classList.remove('backdrop');
      container.current.classList.remove('backdrop--open');
    }
  }, [isOpen]);

  if (!el.current) return null;

  return (
    isOpen &&
    createPortal(
      <div
        ref={backdropRef}
        role="button"
        tabIndex={-1}
        onClick={handleBackdropClick}
      >
        {children}
      </div>,
      el.current
    )
  );
};

Backdrop.propTypes = {
  isOpen: PropTypes.bool,
  closeOnClick: PropTypes.bool,
  handleClose: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired
};

export default Backdrop;
